import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { MapPin, Star, CheckCircle2, HeartHandshake, Eye, Leaf, Shield, Flame, Sparkles, Phone, Clock, Navigation } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import StickyCall from "../components/StickyCall";
import { CallButtons, Breadcrumbs, FaqList, LinkChips } from "../components/PageBlocks";
import { villesSEO, findVille, villesVoisines, REGIONS } from "../villes";
import { cityFaqs } from "../seo/cityContent";
import { servicesSeo } from "../servicesSeo";
import { articles } from "../articles";
import { site } from "../mock";

export default function VilleSEO() {
  const { slug } = useParams();
  const ville = findVille(slug);
  if (!ville) return <Navigate to="/blog" replace />;

  const voisines = villesVoisines(ville);
  const autres = villesSEO.filter((v) => v.slug !== slug && v.region !== ville.region);
  const arts = articles
    .filter((a) => a.villes.includes(slug))
    .slice()
    .sort((a, b) => {
      const prefix = `marabout-${slug}-`;
      const aDed = a.slug.startsWith(prefix) ? 0 : 1;
      const bDed = b.slug.startsWith(prefix) ? 0 : 1;
      return aDed - bDed;
    });
  const lies = arts.length ? arts : articles.slice(0, 3);

  return (
    <div data-testid={`ville-page-${slug}`}>
      <Header />
      <StickyCall />
      <CityHero ville={ville} />
      <ServicesForCity ville={ville} />
      <CityArticles ville={ville} />
      <Quartiers ville={ville} />
      <FaqList title={`Questions fréquentes à ${ville.nom}`} faqs={cityFaqs(ville)} />
      <CityCTA ville={ville} />
      <LinkChips testid="voisines-links" title={`Marabout près de ${ville.nom} — ${regionLabel(ville)}`} links={voisines.map((v) => ({ to: `/marabout/${v.slug}`, label: `Marabout ${v.nom}` }))} />
      <LinkChips testid="services-links" title={`Nos services à ${ville.nom}`} links={[...servicesSeo.map((s) => ({ to: `/${s.slug}`, label: `${s.nav} ${ville.nom}` })), { to: "/retour-affectif", label: `Retour affectif ${ville.nom}` }, { to: "/voyance", label: `Voyant ${ville.nom}` }]} />
      <LinkChips testid="articles-links" title="À lire aussi" links={lies.map((a) => ({ to: `/articles/${a.slug}`, label: a.title }))} />
      <LinkChips testid="autres-villes-links" title="Marabout dans les autres villes" sub="Maître Diamma intervient à Orléans, dans toute la métropole, le Loiret et les villes voisines." links={autres.map((v) => ({ to: `/marabout/${v.slug}`, label: `Marabout ${v.nom}` }))} />
      <Footer />
    </div>
  );
}

const regionLabel = (v) => REGIONS.find((r) => r.key === v.region).label;

function CityHero({ ville }) {
  return (
    <section className="hero-radial py-14 md:py-20">
      <div className="container-narrow">
        <Breadcrumbs items={[{ to: "/blog", label: "Villes desservies" }, { label: `Marabout ${ville.nom}` }]} />
        <div className="grid md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2">
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold-deep mb-3">{ville.cp} • {regionLabel(ville)}</p>
            <h1 data-testid="ville-h1" className="font-display text-3xl md:text-5xl gold-gradient-text mb-4">Marabout & sorcier puissant à {ville.nom}</h1>
            <div className="gold-divider my-5 max-w-[220px]" />
            <p className="text-gold-soft/95 leading-relaxed mb-4">
              Vous cherchez un <strong className="text-gold font-normal">marabout sérieux à {ville.nom}</strong> ou un <strong className="text-gold font-normal">sorcier puissant</strong> capable de défaire la sorcellerie ? Maître Diamma, grand marabout africain {ville.slug === "orleans" ? "installé au centre d'Orléans, intervient dans toute la ville" : `installé à Orléans, intervient à ${ville.nom}`}, {ville.local} : retour affectif, désenvoûtement, magie blanche, voyance et guérison spirituelle.
            </p>
            <p className="text-gold-soft/95 leading-relaxed mb-6">
              Consultations à son cabinet du centre d'Orléans, déplacement à {ville.nom} ({ville.quartiers.slice(0, 3).join(", ")}…) ou à distance par téléphone et WhatsApp. La première prise de contact est <span className="text-gold">gratuite, sans engagement et confidentielle</span>.
            </p>
            <CallButtons id="ville-hero" context={`Marabout ${ville.nom}`} />
          </div>
          <CabinetCard ville={ville} />
        </div>
      </div>
    </section>
  );
}

function CabinetCard({ ville }) {
  return (
    <div className="card-onyx rounded-xl p-6 gold-border-strong">
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="w-5 h-5 text-gold" />
        <span className="font-display text-gold">Cabinet principal</span>
      </div>
      <address className="not-italic text-gold-soft/95 text-sm mb-4">{site.address}</address>
      <div className="text-xs uppercase tracking-widest text-gold-deep mb-2">Se déplace à</div>
      <div className="text-gold-soft text-sm">{ville.nom} ({ville.cp}) & {ville.region === "Voisines" ? "alentours" : "tout le Loiret"}</div>
      <div className="gold-divider my-4" />
      <div className="flex items-center justify-between">
        <div className="flex">
          {[...Array(5)].map((_, i) => <Star key={`cab-s-${i}`} className="w-4 h-4 text-gold" fill="currentColor" />)}
        </div>
        <span className="text-xs text-gold-deep">4.9/5 — 150 avis</span>
      </div>
    </div>
  );
}

function ServicesForCity({ ville }) {
  const n = ville.nom;
  const items = [
    { Ico: Flame, t: `Sorcier puissant à ${n}`, d: `Briser les sorts, neutraliser la sorcellerie et renvoyer le mal envoyé contre vous ou votre famille à ${n}.` },
    { Ico: HeartHandshake, t: `Retour affectif à ${n}`, d: `Faire revenir l'être aimé, sceller votre couple, éloigner un rival et apaiser les disputes.` },
    { Ico: Shield, t: `Désenvoûtement à ${n}`, d: `Libération des envoûtements, du mauvais œil et de la magie noire, puis protection durable du foyer.` },
    { Ico: Sparkles, t: `Magie blanche à ${n}`, d: `Rituels d'amour, de chance, d'argent et de réussite, sans jamais nuire à autrui.` },
    { Ico: Eye, t: `Voyant médium à ${n}`, d: `Voyance précise par oracles africains : amour, travail, argent, santé, famille.` },
    { Ico: Leaf, t: `Guérisseur africain à ${n}`, d: `Soins traditionnels contre angoisses, insomnies, fatigue et blocages inexpliqués.` }
  ];
  return (
    <section className="py-14 md:py-16 bg-charcoal">
      <div className="container-narrow">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display text-2xl md:text-3xl gold-gradient-text">Ce que Maître Diamma fait pour vous à {n}</h2>
          <div className="gold-divider my-5 mx-auto max-w-[180px]" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {items.map(({ Ico, t, d }) => (
            <div key={t} className="card-onyx rounded-xl p-6">
              <div className="w-12 h-12 rounded-lg gold-border flex items-center justify-center mb-4"><Ico className="w-6 h-6 text-gold" /></div>
              <h3 className="font-display text-lg text-gold mb-2">{t}</h3>
              <p className="text-sm text-gold-soft/95 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Block({ title, children }) {
  return (
    <article>
      <h2 className="font-display text-2xl md:text-3xl gold-gradient-text mb-5">{title}</h2>
      <div className="gold-divider mb-6 max-w-[180px]" />
      <div className="space-y-4 text-gold-soft/95 leading-relaxed">{children}</div>
    </article>
  );
}

function CityArticles({ ville }) {
  const n = ville.nom;
  return (
    <section className="py-14 md:py-16 section-vignette">
      <div className="container-narrow max-w-4xl space-y-12">
        <Block title={`Marabout africain à ${n} : un don au service de votre vie`}>
          <p>Maître Diamma est reconnu à {n} comme l'un des <span className="text-gold">marabouts les plus sérieux d'Orléans et du Loiret</span>. Issu d'une lignée de voyants médiums d'Afrique de l'Ouest, il exerce dans le respect des traditions africaines. Chaque consultation est personnalisée, confidentielle et fondée sur la vérité.</p>
          <p>Que vous habitiez {ville.quartiers[0]}, {ville.quartiers[1]} ou le centre de {n}, il vous reçoit à son cabinet d'Orléans ou se déplace à domicile. Vous pouvez aussi consulter à distance avec la même efficacité.</p>
        </Block>
        <Block title={`Sorcier puissant à ${n} : se libérer de la sorcellerie`}>
          <p>La sorcellerie existe à {n} comme partout ailleurs : jalousie autour d'un couple, d'un héritage ou d'une réussite. Malchance qui s'acharne, disputes, cauchemars, maladies qui ne guérissent pas sont des signes fréquents. En véritable <span className="text-gold">sorcier puissant au service du bien</span>, Maître Diamma identifie le travail occulte, le défait et vous protège durablement.</p>
          <p>Il ne pratique jamais la magie noire pour nuire : il la combat. <Link to="/sorcier-puissant-orleans" className="text-gold underline-offset-4 hover:underline">Découvrir son travail de sorcier à Orléans</Link>.</p>
        </Block>
        <Block title={`Retour affectif rapide à ${n}`}>
          <p>Rupture, infidélité, silence : le <Link to="/retour-affectif" className="text-gold hover:underline">rituel de retour affectif</Link> est la demande la plus fréquente à {n}. Maître Diamma rallume les sentiments, répare les malentendus et ramène l'être aimé, souvent en quelques semaines, uniquement par la magie blanche.</p>
        </Block>
        <Block title={`Désenvoûtement et protection à ${n}`}>
          <p>Il détecte la source du mal et procède à un <Link to="/desenvoutement-orleans" className="text-gold hover:underline">désenvoûtement complet</Link>, suivi d'une purification de la maison et d'une protection ancestrale pour vous, votre famille et votre commerce à {n}.</p>
        </Block>
        <Block title={`Pourquoi choisir Maître Diamma à ${n} ?`}>
          <ul className="grid sm:grid-cols-2 gap-3">
            {[`Marabout reconnu à ${n} et dans tout le Loiret`, "35 ans d'expérience, lignée africaine authentique", "Premier contact gratuit, sans engagement", `Déplacement à ${n} et dans les environs`, "Consultation à distance par téléphone / WhatsApp", "Discrétion absolue", "Suivi personnalisé jusqu'au résultat", "Magie blanche uniquement, jamais pour nuire"].map((v) => (
              <li key={v} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-gold mt-0.5 shrink-0" /> {v}</li>
            ))}
          </ul>
        </Block>
      </div>
    </section>
  );
}

function Quartiers({ ville }) {
  return (
    <section data-testid="quartiers-section" className="py-12 bg-charcoal">
      <div className="container-narrow grid md:grid-cols-5 gap-8">
        <div className="md:col-span-3">
          <h2 className="font-display text-2xl gold-gradient-text mb-2">Quartiers desservis à {ville.nom} ({ville.cp})</h2>
          <p className="text-sm text-gold-soft/85 mb-5">Maître Diamma se déplace dans tous les quartiers de {ville.nom} et dans un rayon de 15 km autour de la ville.</p>
          <ul className="flex flex-wrap gap-2">
            {ville.quartiers.map((q) => (
              <li key={q} className="text-xs px-3 py-1.5 rounded-full gold-border text-gold-soft/95 flex items-center gap-1"><MapPin className="w-3 h-3 text-gold" /> {q}</li>
            ))}
          </ul>
        </div>
        <LocalCard ville={ville} />
      </div>
    </section>
  );
}

function LocalCard({ ville }) {
  const rows = [
    { Ico: MapPin, label: "Zone d'intervention", value: `${ville.nom} (${ville.cp}) et environs`, id: "zone" },
    { Ico: Phone, label: "Téléphone & WhatsApp", value: site.phone, href: `tel:${site.phoneRaw}`, id: "phone" },
    { Ico: Clock, label: "Horaires", value: "7j/7 — de 8h à 22h", id: "hours" },
    { Ico: Navigation, label: "Coordonnées GPS", value: `${ville.lat.toFixed(4)}, ${ville.lng.toFixed(4)}`, id: "gps" }
  ];
  return (
    <div data-testid="local-card" className="md:col-span-2 card-onyx rounded-xl p-6 gold-border-strong">
      <h3 className="font-display text-lg text-gold mb-4">Maître Diamma à {ville.nom}</h3>
      <dl className="space-y-3 text-sm">
        {rows.map(({ Ico, label, value, href, id }) => (
          <div key={id} data-testid={`local-${id}`} className="flex items-start gap-3">
            <Ico className="w-4 h-4 text-gold mt-0.5 shrink-0" />
            <div>
              <dt className="text-[11px] uppercase tracking-widest text-gold-deep">{label}</dt>
              <dd className="text-gold-soft/95">{href ? <a href={href} className="hover:text-gold">{value}</a> : value}</dd>
            </div>
          </div>
        ))}
      </dl>
    </div>
  );
}

function CityCTA({ ville }) {
  return (
    <section className="py-14 hero-radial">
      <div className="container-narrow text-center max-w-2xl mx-auto">
        <Sparkles className="w-8 h-8 text-gold mx-auto mb-3" />
        <h2 className="font-display text-2xl md:text-4xl gold-gradient-text mb-4">Reprenez votre destinée en main à {ville.nom}</h2>
        <p className="text-gold-soft/95 mb-6">Un simple appel gratuit peut tout changer. Maître Diamma vous écoute avec bienveillance et vous indique la voie.</p>
        <CallButtons id="ville-cta" center context={`Marabout ${ville.nom}`} />
      </div>
    </section>
  );
}
